import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
} from "@mui/material";

import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import axios from "axios";
import SowPrintingReportModal from "./SowPrintingReportModal.js";
import ArrowCircleDownIcon from "@mui/icons-material/ArrowCircleDown";
import { toPng } from "html-to-image";

const fieldMappings = {
  ageOfMachine: "Age of Machine",
  ampereOfMachine: "Ampere of Machine",
  controllerSetting: "Controller Setting",
  subCategory: "Sub Category",
  type: "Machine Type",
  breadthFoot: "Breadth In Foot",
  lengthFoot: "Length In Foot",
};

const keyMappingsElectrical = {
  lightSourcebrand: "Light Source Brand",
  lightSourcecolorTemp: "Light Source Color Temp",
  lightSourceqty: "Light Source Quantity",
  lightSourcemodelNo: "Light Source Model No.",
  lightSourcetype: "Light Source Type ",
  lightSourcewattage: "Light Source Wattage",
};
const keyMappingsMechanical = {
  applicationPlace: "Application Place",
  backinggrade: "Backing Grade",
  backingmaterial: "Backing Material",
  backingthickness: "Backing Thickness",
  face: "Face Type",
  frameDetailsections: "Frame Sections",
  frameDetailwallThickness: "Wall Thickness",
  raisinggrade: "Raising Grade",
  raisingheight: "Raising Height",
  raisingmaterial: "Raising Material",
  raisingthickness: "Raising Thickness",
  size: "Size",
};

// Define fields to exclude (e.g., empty fields or unwanted fields)
const excludedFields = [
  "cadFile",
  "specialMentions",
  "wiring",
  "lightSourcecolorTemp",
  "lightSourcemodelNo",
  "lightSourcewattage",
  "lightSourcetype",
  "lightSourcebrand",
  "lightSourceqty",
  "frameDetailweight",
  "specificationFile",
  "paint",
];

const SowReportDialog = ({
  open,
  onClose,
  // setFabReportRef,
  campaignId,
  rfpData,
  createdSowId,
  scopeOfWorks,
  selectedRow,
  setScopeOfWorks,
}) => {
  //first logic of if updatedSowData

  const sowFabReportRef = useRef(null);
  const sowSecondFabRef = useRef(null);

  const sowPrintReportRef = useRef(null);
  const sowSecondPrintRef = useRef(null);

  const [pdfBlobState, setPdfBlobState] = useState(null);
  const [pdfPrintBlobState, setPrintPdfBlobState] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [reportData, setReportData] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const serverURL = process.env.REACT_APP_URL;
  const [openPrinting, setOpenPrinting] = useState(false);

  const getSowReportData = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${serverURL}/requestForProposal/viewSowReport/${
          createdSowId || selectedRow
        }`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.data.success) {
        setReportData(response.data.data);
      }
    } catch (error) {
      console.error("Error detecting", error);
    }
  };

  const generateAndUploadSowPDF = async ({
    ref,
    secondRef,
    reportType,
    pdfBlobSetter,
  }) => {
    try {
      setUploading(true);
      window.onbeforeunload = () => "Please wait, report is being uploaded...";

      const element = ref?.current;
      const element2 = secondRef?.current;
      const token = localStorage.getItem("token");

      // If ref is null, send an empty string to the backend
      if (!element) {
        console.warn(`⚠️ Skipping ${reportType} PDF generation: ref is null`);

        setScopeOfWorks((prev) => {
          const updatedScopeOfWorks = prev.map((item) =>
            item.id === 1
              ? {
                  ...item,
                  ...(reportType === "fabrication"
                    ? { sowFabricationReport: "" }
                    : { sowPrintingReport: "" }),
                }
              : item
          );
          axios
            .put(
              `${serverURL}/product/updateSOW/${createdSowId}`,
              { scopeOfWorks: updatedScopeOfWorks, campaignId, rfpData },
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            )
            .catch((err) =>
              console.error(`❌ Error updating empty ${reportType} SOW:`, err)
            );

          return updatedScopeOfWorks;
        });

        return; // ✅ Skip rest of the function
      }

      // If ref is valid, proceed with image generation
      const dataUrl = await toPng(element, {
        cacheBust: true,
        useCORS: true,
      });
      const dataUrlSecond = await toPng(element2, {
        cacheBust: true,
        useCORS: true,
      });
      const images = [dataUrl, dataUrlSecond]; // Store both images

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "px",
        format: "a4",
        compress: true,
      });

      const marginX = 12;
      const marginY = 12;
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const availableWidth = pageWidth - 2 * marginX;
      const availableHeight = pageHeight - 2 * marginY;

      for (let index = 0; index < images.length; index++) {
        const img = new Image();
        img.src = images[index];

        await new Promise((resolve) => {
          img.onload = () => {
            const imgWidth = img.width;
            const imgHeight = img.height;
            const widthRatio = availableWidth / imgWidth;
            const heightRatio = availableHeight / imgHeight;
            const scale = Math.min(widthRatio, heightRatio, 1); // maintain aspect ratio, avoid upscaling

            const scaledWidth = imgWidth * scale;
            const scaledHeight = imgHeight * scale;

            // Always start from top-left margin
            const offsetX = marginX;
            const offsetY = marginY;
            let position = 0;

            while (position < scaledHeight) {
              const canvas = document.createElement("canvas");
              const context = canvas.getContext("2d");
              const sliceHeight = Math.min(imgHeight, availableHeight / scale);
              canvas.width = imgWidth;
              canvas.height = sliceHeight;

              context.drawImage(
                img,
                0,
                position / scale,
                imgWidth,
                sliceHeight,
                0,
                0,
                imgWidth,
                sliceHeight
              );

              const pageDataUrl = canvas.toDataURL("image/png");

              if (index !== 0 || position > 0) {
                pdf.addPage();
              }
              pdf.addImage(
                pageDataUrl,
                "PNG",
                offsetX,
                offsetY,
                scaledWidth,
                scaledHeight
              );
              position += availableHeight;
            }

            resolve();
          };
        });
      }

      // Convert to blob
      const pdfBlob = pdf.output("blob");
      pdfBlobSetter(pdfBlob);

      const pdfFile = new File([pdfBlob], "sowReport.pdf", {
        type: "application/pdf",
      });

      const formData = new FormData();
      formData.append("files", pdfFile);
      const response = await axios.post(
        `${process.env.REACT_APP_URL}/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.success) {
        const uploadedUrl = response.data.data[0];
        await setPdfUrl(uploadedUrl);

        setScopeOfWorks((prev) => {
          const updatedScopeOfWorks = prev.map((item) =>
            item.id === 1
              ? {
                  ...item,
                  ...(reportType === "fabrication"
                    ? { sowFabricationReport: uploadedUrl }
                    : { sowPrintingReport: uploadedUrl }),
                }
              : item
          );

          axios
            .put(
              `${serverURL}/product/updateSOW/${createdSowId}`,
              { scopeOfWorks: updatedScopeOfWorks },
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            )
            .catch((err) =>
              console.error(`❌ Error updating ${reportType} SOW:`, err)
            );

          return updatedScopeOfWorks;
        });
      } else {
        console.error("❌ Upload failed:", response.data.message);
      }
    } catch (error) {
      console.error(
        `❌ Error generating or uploading ${reportType} PDF:`,
        error
      );
    } finally {
      setUploading(false);
      window.onbeforeunload = null;
    }
  };

  const downloadFabPDF = () => {
    if (!pdfBlobState) {
      console.error("No PDF generated yet!");
      return;
    }

    const url = URL.createObjectURL(pdfBlobState);
    const link = document.createElement("a");
    link.href = url;
    link.download = "Fabrication.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadPrintPDF = () => {
    if (!pdfPrintBlobState) {
      console.error("No PDF generated yet!");
      return;
    }

    const url = URL.createObjectURL(pdfPrintBlobState);
    const link = document.createElement("a");
    link.href = url;
    link.download = "Printing.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    const uploadReports = async () => {
      if (createdSowId) {
        // await handleDeleteUpload();

        await generateAndUploadSowPDF({
          ref: sowPrintReportRef,
          secondRef: sowSecondPrintRef,

          reportType: "printing",
          pdfBlobSetter: setPrintPdfBlobState,
        });

        await generateAndUploadSowPDF({
          ref: sowFabReportRef,
          secondRef: sowSecondFabRef,
          reportType: "fabrication",
          pdfBlobSetter: setPdfBlobState,
        });
      }
    };

    setTimeout(() => {
      uploadReports(); // 🟢 call the merged function for both
    }, 1000);

    getSowReportData();
  }, [open]);

  const hasPrinting =
    reportData?.servicesNeeded?.some((ele) => ele.includes("Printing")) ??
    false; // Ensure it doesn't return undefined

  useEffect(() => {
    if (hasPrinting) {
      setOpenPrinting(true);
    }
  }, [hasPrinting]);

  const hasFabrication =
    reportData?.servicesNeeded?.some((ele) => ele.includes("Fabrication")) ??
    false;

  const handleDownload = async (fileUrl, type) => {
    if (!fileUrl) return;

    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob(); // Convert to a Blob
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      if (type == "print") {
        link.setAttribute("download", "Printing.pdf");
      } else {
        link.setAttribute("download", "Fabrication.pdf"); // Set file name
      }
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle className="text-xl font-semibold flex justify-between items-center">
        <h1>Scope Of Work Report</h1>{" "}
        <div>
          {uploading && (
            <div className="fixed inset-0 z-[99] flex items-center justify-center bg-white/80">
              <div className="bg-white p-6 md:p-8 rounded-xl shadow-lg text-center max-w-sm w-full">
                <p className="text-lg font-medium text-gray-700 mb-4">
                  ⏳ Uploading PDF... <br />
                  Please don’t refresh or close the tab.
                </p>
                <div className="flex justify-center">
                  <CircularProgress size={48} thickness={4} color="primary" />
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          {reportData?.sowFabricationReport ? (
            <button
              onClick={() =>
                handleDownload(reportData?.sowFabricationReport, "fab")
              }
              className="p-2 bg-blue-500 text-white rounded text-[14px]"
            >
              <ArrowCircleDownIcon className="mr-1" color="inherit" size={15} />
              Download Fabrication
            </button>
          ) : (
            hasFabrication && (
              <button
                onClick={downloadFabPDF}
                className=" p-2 bg-blue-500 text-white rounded  text-[14px]"
              >
                <ArrowCircleDownIcon
                  className="mr-1"
                  color="inherit"
                  size={15}
                />
                Download Fab Report
              </button>
            )
          )}
          {reportData?.sowPrintingReport ? (
            <button
              onClick={() =>
                handleDownload(reportData?.sowPrintingReport, "print")
              }
              className="p-2 bg-blue-500 text-white rounded text-[14px]"
            >
              <ArrowCircleDownIcon className="mr-1" color="inherit" size={15} />
              Download Printing
            </button>
          ) : (
            hasPrinting && (
              <button
                onClick={downloadPrintPDF}
                className=" p-2 bg-blue-500 text-white rounded text-[14px]"
              >
                <ArrowCircleDownIcon
                  className="mr-1"
                  color="inherit"
                  size={15}
                />
                Download Print Report
              </button>
            )
          )}
        </div>
      </DialogTitle>
      <DialogContent>
        {hasFabrication && (
          <div>
            <h2 className="text-lg text-[var(--primary-color)] font-bold">
              Fabrication Report :-
            </h2>

            <div
              className="p-6 border border-black-900 rounded-md"
              ref={sowFabReportRef}
            >
              <div className="flex justify-between items-center mb-4">
                <div className="flex justify-center items-center">
                  <div>
                    <img
                      src="/assests/images/pixell dark.png"
                      width={80}
                      height={80}
                      alt="logo"
                    />
                  </div>
                  <div className="mx-4">
                    {/* <h2 className="text-4xl font-bold">Pixell</h2> */}
                    <p className="text-sm text-gray-500">
                      Campaign Id: #{reportData?.campaignId?.slice(-6)}
                    </p>
                    <p className="text-sm text-gray-500">support@pixell.in</p>
                  </div>
                </div>
                <div className="text-start">
                  <h2 className="text-3xl text-black font-semibold">
                    Scope Of Work
                  </h2>
                </div>
              </div>
              <div className=" flex justify-between border-t border-b border-black pt-4 pb-4">
                <div className="border-l border-black px-4">
                  <h3 className="text-lg font-semibold">Product:</h3>
                  <p>
                    <span className="text-sm font-light">
                      Product Category:
                    </span>{" "}
                    {reportData?.productDetails?.productType}
                  </p>
                  <p>
                    <span className="text-sm font-light">Service:</span>{" "}
                    Fabrication
                  </p>
                  <p>
                    <span className="text-sm font-light">Weight:</span>{" "}
                    {reportData?.materialDetailsForFabrication
                      ?.frameDetailweight || "0kg"}
                  </p>
                </div>
                <div className="flex">
                  <div className="mx-6">
                    <img
                      src={
                        reportData?.productDetails?.printImage ||
                        "/assests/AdminDashboard/userForIP.webp"
                      }
                      className="w-64 h-32"
                      alt="logo"
                    />
                  </div>
                  <div className="mx-6">
                    <img
                      src={
                        reportData?.productDetails?.productSecondImg ||
                        "/assests/AdminDashboard/userForIP.webp"
                      }
                      className="w-64 h-32"
                      alt="logo"
                    />
                  </div>
                </div>
              </div>
              <div className=" w-full mt-5">
                <h3 className="text-xl font-semibold mb-3">Description</h3>
                <p className="border rounded-md p-3 text-[14px] break-words">
                  {reportData?.description
                    ?.split("•")
                    .filter((line) => line.trim() !== "")
                    .map((line, idx) => (
                      <p key={idx}>
                        {idx + 1}. {line.trim()}
                      </p>
                    ))}
                </p>
              </div>
              <div className="flex justify-start gap-3 items-start py-2">
                <div className="w-[50%] ">
                  <h1>Mechanical Specs</h1>

                  <table className=" w-full border-collapse mt-4 border text-sm">
                    <thead>
                      <tr className="bg-black text-white">
                        <th className="border text-start p-2">S. No.</th>
                        <th className="border text-start p-2">Heading</th>
                        <th className="border text-start p-2">
                          Specifications
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData?.materialDetailsForFabrication &&
                        Object.entries(reportData.materialDetailsForFabrication)
                          .filter(
                            ([key, value]) => !excludedFields.includes(key)
                          ) // Remove empty and unwanted fields
                          .map(([key, value], index) => (
                            <tr
                              key={key}
                              className={index % 2 !== 0 ? "bg-gray-100" : ""}
                            >
                              <td className="border p-2">{index + 1}</td>
                              <td className="border p-2">
                                {keyMappingsMechanical[key] || key}{" "}
                                {/* Rename if available */}
                              </td>
                              <td className="border p-2"> {value || "N/A"}</td>
                            </tr>
                          ))}
                    </tbody>
                  </table>
                </div>
                <div className="w-[50%]">
                  <h1>Electrical Specs</h1>
                  <table className="w-full border-collapse mt-4 border text-sm">
                    <thead>
                      <tr className="bg-black text-white">
                        <th className="border text-start p-2">S. No.</th>
                        <th className="border text-start p-2">Heading</th>
                        <th className="border text-start p-2">
                          Specifications
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData?.electricalDetails &&
                        Object.entries(reportData.electricalDetails).map(
                          ([key, value], index) => (
                            <tr
                              key={key}
                              className={index % 2 !== 0 ? "bg-gray-100" : ""}
                            >
                              <td className="border p-2">{index + 1}</td>
                              <td className="border p-2">
                                {keyMappingsElectrical[key] || key}{" "}
                                {/* Use custom name if available */}
                              </td>
                              <td className="border p-2"> {value || "N/A"}</td>
                            </tr>
                          )
                        )}
                    </tbody>
                  </table>

                  <div className=" w-full mt-5">
                    <h3 className="text-xl font-semibold mb-3">Paint</h3>
                    <p className="border rounded-md p-3 text-[14px]">
                      {reportData?.materialDetailsForFabrication?.paint ||
                        "No Data Available"}
                    </p>
                  </div>
                </div>
              </div>
              <h3 className="text-xl font-semibold my-3">Approved Brands</h3>
              <table className="w-full border-collapse mt-4 border text-sm">
                <thead>
                  <tr className="bg-black text-white">
                    <th className="border text-start p-2">S. No.</th>
                    <th className="border text-start p-2">PRODUCT</th>
                    <th className="border text-start p-2">APPROVED BRAND</th>
                    <th className="border text-start p-2">MODEL NO.</th>
                  </tr>
                </thead>
                <tbody>
                  {[...Array(8)].map((_, i) => (
                    <tr key={i} className={i % 2 != 0 ? "bg-gray-100" : ""}>
                      <td className="border p-2 ">{i + 1}</td>
                      <td className="border p-2">
                        {
                          [
                            "Tubelight",
                            "LED Model",
                            "Power Supply",
                            "Tubelight",
                            "Wire",
                            "Wire",
                            "Backlight",
                            "MS",
                          ][i % 8]
                        }
                      </td>
                      <td className="border p-2">
                        {
                          [
                            "Philips",
                            "Osram",
                            "Samsung",
                            "Polycab",
                            "Polycab",
                            "RR Kabel",
                            "LG Lucky Flex",
                            "Jindal Steel",
                          ][i % 8]
                        }
                      </td>
                      <td className="border p-2">
                        {
                          [
                            "TL-D90",
                            "1.5W (6500K)",
                            "200W IP-65",
                            "20W, 1130mm, 6500K",
                            "Copper-1.5mm",
                            "Fab-2.5Sq mm, (2.5mm)",
                            "620 gsm",
                            "0.5mm,24 Gauge",
                          ][i % 8]
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div
              ref={sowSecondFabRef}
              className="p-6 border border-black-900 rounded-md"
            >
              <h3 className="text-lg font-semibold ">
                Fabrication Machines Required
              </h3>
              <div className="grid grid-cols-2 gap-4 mt-2">
                {reportData?.machinesNeededForFabrication?.length === 0 ? (
                  <p className="border rounded-md p-3 text-[14px]">
                    No Machine Required
                  </p>
                ) : (
                  reportData?.machinesNeededForFabrication?.map((i) => (
                    <div
                      key={i}
                      className="border shadow-md bg-white rounded-t-md"
                    >
                      <h4 className="font-semibold text-lg border-b py-1  bg-black px-4 text-white rounded-t-md">
                        {i?.type}
                      </h4>
                      <table className="w-full border-collapse border text-sm">
                        <tbody>
                          {i?.details &&
                            Object.entries(i.details).map(
                              ([key, value], index) => {
                                // Check if the field exists in the mapping; otherwise, fallback to key formatting
                                const displayName =
                                  fieldMappings[key] ||
                                  key.replace(/([A-Z])/g, " $1");

                                return (
                                  <tr
                                    key={index}
                                    className={
                                      index % 2 !== 0 ? "bg-gray-100" : ""
                                    }
                                  >
                                    <td className="border p-2">{index + 1}</td>
                                    <td className="border p-2">
                                      {displayName}
                                    </td>
                                    <td className="border p-2">{value}</td>
                                  </tr>
                                );
                              }
                            )}
                        </tbody>
                      </table>
                    </div>
                  ))
                )}
              </div>
              <section className="py-2">
                <h1 className="my-2">Fabrication Guidelines</h1>
                <div className="w-full p-2 border rounded-md">
                  {reportData?.fabricationGuidelines
                    ?.split("•")
                    .filter((line) => line.trim() !== "")
                    .map((line, idx) => (
                      <p key={idx}>
                        {idx + 1}. {line.trim()}
                      </p>
                    ))}
                </div>
              </section>
              <section className="py-2">
                <h1 className="my-2">PDI Guidelines</h1>
                <div className="w-full p-2 border rounded-md">
                  {reportData?.pdiGuidelines
                    ?.split("•")
                    .filter((line) => line.trim() !== "")
                    .map((line, idx) => (
                      <p key={idx}>
                        {idx + 1}. {line.trim()}
                      </p>
                    ))}
                </div>
              </section>
              <section className="py-2">
                <h1 className="my-2">Packaging Guidelines</h1>
                <div className="w-full p-2 border rounded-md">
                  {reportData?.packagingGuidelines
                    ?.split("•")
                    .filter((line) => line.trim() !== "")
                    .map((line, idx) => (
                      <p key={idx}>
                        {idx + 1}. {line.trim()}
                      </p>
                    ))}
                </div>
              </section>
              <section className="py-2">
                <h1 className="my-2">Delivery Guidelines</h1>
                <div className="w-full p-2 border rounded-md">
                  {reportData?.deliveryGuidelines
                    ?.split("•")
                    .filter((line) => line.trim() !== "")
                    .map((line, idx) => (
                      <p key={idx}>
                        {idx + 1}. {line.trim()}
                      </p>
                    ))}{" "}
                </div>
              </section>
            </div>
          </div>
        )}
        {hasPrinting && (
          <SowPrintingReportModal
            open={openPrinting}
            sowPrintReportRef={sowPrintReportRef}
            sowSecondPrintRef={sowSecondPrintRef}
            createdSowId={createdSowId || selectedRow}
          />
        )}
        {!hasPrinting && !hasFabrication && (
          <div className="flex justify-center items-center h-[150px] border">
            <h2>
              There is no Fabrication and Printing Service in this Campaign
            </h2>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SowReportDialog;
